import { Box, Flex, FlexProps } from "@chakra-ui/react"
import { Layout } from "DesignSystem/components"
import React from "react"
import { Helmet } from "react-helmet"

import { BasicFlash } from "Components/basic-flash/basic-flash"
import { useFeatureFlag } from "Hooks/use-feature-flag"
import { getIsPanelistDomain } from "Shared/helpers/getIsPanelistDomain"

interface PageProps extends FlexProps {
  title?: string
  // Set this to avoid rendering the <BasicFlash /> component inside since it requires a Redux store
  noBasicFlash?: boolean
  includeTitlePrefix?: boolean
}

/**
 * Handles the page <title> and (for now) legacy uses of <BasicFlash>.
 *
 * Please avoid adding styles that make assumptions about page layout, e.g.
 * spacing, as that sort of less-composable approach can make future layout
 * changes harder.
 */
export const Page: React.FC<React.PropsWithChildren<PageProps>> = ({
  title,
  noBasicFlash = false,
  includeTitlePrefix = true,
  children,
  ...props
}) => {
  return (
    <>
      {title &&
        (includeTitlePrefix ? (
          <Helmet>
            <title>{title}</title>
          </Helmet>
        ) : (
          <Helmet titleTemplate="%s">
            <title>{title}</title>
          </Helmet>
        ))}
      {shouldShowSidebarNav() ? (
        <Box>{children}</Box>
      ) : (
        <LegacyPageWrapper {...props}>{children}</LegacyPageWrapper>
      )}
      {noBasicFlash ? null : <BasicFlash />}
    </>
  )
}

/**
 * @deprecated To be deleted once we remove the sidebar nav flag.
 */
const LegacyPageWrapper: React.FC<React.PropsWithChildren<FlexProps>> = ({
  children,
  ...props
}) => {
  return (
    <Flex direction="column" minH="100vh" {...props}>
      {children}
    </Flex>
  )
}

/**
 * @deprecated Use the Design System {@link Layout.Page} instead.
 */
export const PageMain: React.FC<React.PropsWithChildren<FlexProps>> = ({
  sx = {},
  children,
  ...props
}) => (
  <Flex
    direction={{ base: "column", lg: "row" }}
    justify={{ base: "start", lg: "center" }}
    sx={{ ...sx }}
    flexGrow={1}
    {...props}
  >
    {children}
  </Flex>
)

/**
 * @deprecated Use the Design System {@link Layout.Page} instead.
 */
export const PageContent: React.FC<React.PropsWithChildren<FlexProps>> = ({
  sx = {},
  children,
  ...props
}) => (
  <Box
    sx={{ ...sx }}
    flexGrow={1}
    px={{
      base: 6,
      md: 8,
      lg: 10,
    }}
    py={6}
    {...props}
  >
    {children}
  </Box>
)

const shouldShowSidebarNav = () => {
  const isSidebarNavEnabled = useFeatureFlag("sidebar_nav")
  // The Page component is used on UserCrowd too, so we need to support
  // LegacyPage for now until we remove the sidebar nav flag
  return isSidebarNavEnabled && !getIsPanelistDomain()
}
